<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  date: { type: String, required: true },
});
const emit = defineEmits(['changeDate', 'newAppointment']);

const store = useStore();
const route = useRoute();
const { t, locale } = useI18n();

const viewMode = computed(() => store.getters['painelView/getViewMode']);
const calendarState = computed(
  () => store.getters['painelCalendar/getCalendarState']
);
const connectUrl = computed(
  () => `/omniauth/google_calendar?account_id=${route.params.accountId}`
);

const today = computed(() => new Date().toLocaleDateString('en-CA'));
const goToday = () => emit('changeDate', today.value);

const shift = days => {
  const d = new Date(`${props.date}T00:00:00`);
  d.setDate(d.getDate() + days);
  emit('changeDate', d.toLocaleDateString('en-CA'));
};

const dateLabel = computed(() =>
  new Date(`${props.date}T00:00:00`).toLocaleDateString(
    locale.value.replace('_', '-'),
    {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }
  )
);

const setMode = m => store.dispatch('painelView/setViewMode', m);

const prevArrow = '‹';
const nextArrow = '›';
</script>

<template>
  <header
    class="flex flex-wrap items-center justify-between gap-3 border-b border-n-strong bg-n-surface-1 px-5 py-3"
  >
    <!-- Left: nav buttons + date label (desktop only) -->
    <div class="flex items-center gap-3">
      <button
        class="rounded border border-n-strong px-3 py-1 text-sm"
        @click="goToday"
      >
        {{ t('PAINEL.APPOINTMENTS.TODAY') }}
      </button>
      <div class="flex">
        <button
          class="rounded-l border border-n-strong px-2 py-1"
          :aria-label="t('PAINEL.APPOINTMENTS.MINI_CAL.PREV')"
          @click="shift(-1)"
        >
          {{ prevArrow }}
        </button>
        <button
          class="rounded-r border border-l-0 border-n-strong px-2 py-1"
          :aria-label="t('PAINEL.APPOINTMENTS.MINI_CAL.NEXT')"
          @click="shift(1)"
        >
          {{ nextArrow }}
        </button>
      </div>
      <div
        class="hidden text-base font-semibold capitalize text-n-slate-12 md:block"
      >
        {{ dateLabel }}
      </div>
    </div>

    <!-- Right: mobile (date label + date picker) / desktop (connect + toggle + new) -->
    <div class="flex items-center gap-3">
      <!-- Mobile only: full date label where new appointment button was -->
      <div class="text-sm font-semibold capitalize text-n-slate-11 md:hidden">
        {{ dateLabel }}
      </div>
      <!-- Mobile only: native date picker -->
      <input
        type="date"
        :value="date"
        class="rounded border border-n-strong bg-n-surface-1 px-2 py-1 text-sm text-n-slate-12 md:hidden"
        @change="e => emit('changeDate', e.target.value)"
      />
      <!-- Desktop only: connect Google Calendar -->
      <a
        v-if="!calendarState.connected"
        :href="connectUrl"
        class="hidden items-center gap-1.5 rounded border border-amber-400 px-3 py-1.5 text-sm font-semibold text-amber-600 md:flex"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        {{ t('PAINEL.APPOINTMENTS.HEADER.CONNECT_CALENDAR') }}
      </a>
      <!-- Desktop only: day / list / agenda view toggle -->
      <div class="hidden gap-1 rounded bg-n-slate-3 p-0.5 md:flex">
        <button
          class="rounded px-3 py-1 text-sm"
          :class="
            viewMode === 'day'
              ? 'bg-n-surface-1 font-semibold shadow-sm'
              : 'text-n-slate-11'
          "
          @click="setMode('day')"
        >
          {{ t('PAINEL.APPOINTMENTS.HEADER.VIEW_DAY') }}
        </button>
        <button
          class="rounded px-3 py-1 text-sm"
          :class="
            viewMode === 'list'
              ? 'bg-n-surface-1 font-semibold shadow-sm'
              : 'text-n-slate-11'
          "
          @click="setMode('list')"
        >
          {{ t('PAINEL.APPOINTMENTS.HEADER.VIEW_LIST') }}
        </button>
        <button
          class="rounded px-3 py-1 text-sm"
          :class="
            viewMode === 'agenda'
              ? 'bg-n-surface-1 font-semibold shadow-sm'
              : 'text-n-slate-11'
          "
          @click="setMode('agenda')"
        >
          {{ t('PAINEL.APPOINTMENTS.HEADER.VIEW_AGENDA') }}
        </button>
      </div>
      <!-- Desktop only: new appointment button -->
      <button
        class="hidden rounded bg-n-brand px-3 py-1.5 text-sm font-semibold text-white md:block"
        @click="emit('newAppointment')"
      >
        {{ t('PAINEL.APPOINTMENTS.HEADER.NEW_APPOINTMENT') }}
      </button>
    </div>
  </header>
</template>
